import * as React from 'react'
import { Alert, Box, Button, Card, CardContent, CardHeader, Container, Grid2 as Grid, TextField, Typography } from '@mui/material'
import TitleBar from 'components/TitleBar'
import useFetchWithAuth from 'hooks/useFetchWithAuth'
import { ErrorContext } from 'contexts/errorContext'

/**
 * Account component for displaying form
 */
const Account = () => {
  const [formData, setFormData] = React.useState({ firstName: '', lastName: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '' })
  const [formErrors, setFormErrors] = React.useState({})
  const [alert, setAlert] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const { showError } = React.useContext(ErrorContext)
  const fetchWithAuth = useFetchWithAuth()

  // Fetches user details
  React.useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await fetchWithAuth('/api/account/profile')
        const user = await response.json()
        setFormData(user)
      } catch (error) {
        showError('Failed to load user details. Please try again or refresh the page.')
        console.error(error.message)
      }
    }

    fetchUserDetails()
  }, [])

  // Handles form changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormErrors({})
    setAlert(null)
    setIsLoading(true)

    try {
      const response = await fetchWithAuth('/api/account/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      const json = await response.json()
  
      if (response.ok) {
        setFormData({ ...formData, currentPassword: '', newPassword: '', confirmPassword: '' })
        setAlert({ type: 'success', message: 'Profile updated successfully!' })
      } else {
        if ('errors' in json) {
          setFormErrors(json.errors)
        } else {
          setAlert({ type: 'error', message: 'Something went wrong. Please try again.' })
        }
      }
    } catch (error) {
      showError('Failed to update user details. Please try again or refresh the page.')
      console.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TitleBar title="My Account" actions={[
        <Button variant="contained" type="submit" disabled={isLoading}>Save</Button>
      ]} />
      <Container maxWidth="xl">
        {alert && (
          <Alert severity={alert.type} sx={{ mt: 6, border: 1, borderColor: alert.type + '.light' }}>
            {alert.message}
          </Alert>
        )}
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ sm: 12, md: 8 }}>
            {/* Personal info section */}
            <Card variant="outlined" sx={{ my: 6 }}>
              <CardHeader title="Personal Information" />
              <CardContent>
                <Typography>This is your personal information to identify the account.</Typography>
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={!!formErrors.firstName}
                      helperText={formErrors.firstName}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={!!formErrors.lastName}
                      helperText={formErrors.lastName}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="email"
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Change password section */}
            <Card variant="outlined" sx={{ my: 6 }}>
              <CardHeader title="Change Password" />
              <CardContent>
                <Typography>If you want to change password, please fill the inputs otherwise leave them blank.</Typography>
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="password"
                      label="Current Password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      error={!!formErrors.currentPassword}
                      helperText={formErrors.currentPassword}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        type="password"
                        label="New Password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        error={!!formErrors.newPassword}
                        helperText={formErrors.newPassword}
                      />
                  </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="password"
                      label="Re-enter Password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      error={!!formErrors.confirmPassword}
                      helperText={formErrors.confirmPassword}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Account